// ButtonTabs.tsx
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import type { ButtonVariant } from 'react-bootstrap/esm/types';

export type ButtonTabKey = string;

export interface ButtonTabConfig<K extends ButtonTabKey = ButtonTabKey> {
  key: K;
  label: React.ReactNode;
  activeVariant?: ButtonVariant;
}

export interface ButtonTabsProps<K extends ButtonTabKey = ButtonTabKey> {
  tabs: readonly ButtonTabConfig<K>[];
  activeKey: K;
  onSelect: (key: K) => void;
  inactiveVariant?: ButtonVariant;
  size?: 'sm' | 'lg';
  className?: string;
}

export const ButtonTabs = <K extends ButtonTabKey>({
  tabs,
  activeKey,
  onSelect,
  inactiveVariant = 'secondary',
  size,
  className,
}: ButtonTabsProps<K>) => {
  return (
    <ButtonGroup role="tablist" aria-label="Button tabs" size={size} className={className}>
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        const variant: ButtonVariant = isActive
          ? (tab.activeVariant ?? 'primary')
          : (inactiveVariant ?? 'secondary');

        return (
          <Button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            variant={variant}
            onClick={() => onSelect(tab.key)}
          >
            {tab.label}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};
